import {
  EGameConfigs,
  IMapEntityBotLoose,
  IMapEntityLoose,
  IMatchConfigEntry,
  IMatchConfigEntryOrLoose,
  IMatchConfigEntryTf,
  IMatchConfigEntryZr,
  IRoomLoose,
  ISpawnConfigLoose,
  ISpawnSetLoose,
  MatchType,
  MatchTypeWire
} from '@nova/types';
import { ReadonlyDeep, WritableDeep } from 'type-fest';

import { matchConfigOrLiteral, matchConfigTfLiteral, matchConfigZrLiteral } from '../configs';

import { EntityDatasetKey } from './entity-types';

// ==============================
//  Strict Map Entity Types
// ==============================

/**
 * Map entity with type-safe entity dataset key.
 */
export interface IMapEntity extends Omit<IMapEntityLoose, 'id'> {
  id: EntityDatasetKey;
}

/**
 * Map entity bot with type-safe entity dataset key.
 */
export interface IMapEntityBot extends Omit<IMapEntityBotLoose, 'id'> {
  id: EntityDatasetKey;
}

export interface ISpawnSet extends Omit<ISpawnSetLoose, 'entities'> {
  entities: IMapEntity[];
}

export interface ISpawnConfig extends Omit<ISpawnConfigLoose, 'spawnSets'> {
  spawnSets: ISpawnSet[];
}

export interface IRoom extends Omit<IRoomLoose, 'initEntities' | 'initBots'> {
  initEntities: IMapEntity[];
  initBots?: IMapEntityBot[];
}

export type RoomSet = { [roomIndex: string]: IRoom | undefined };

export interface IMatchConfigEntryOr extends Omit<IMatchConfigEntryOrLoose, 'rooms'> {
  rooms: RoomSet;
}

export function isMatchConfigEntryOr(matchConfig: IMatchConfigEntry): matchConfig is IMatchConfigEntryOr {
  // MatchType values are defined equal to MatchTypeWire literals (outpostRuns === 'trenchRun'); cast is TS-only.
  return matchConfig.type === (MatchType.outpostRuns as MatchTypeWire)
    || 'rooms' in matchConfig;
}

// ==============================
//  Match Config Literals
// ==============================

matchConfigOrLiteral satisfies ReadonlyDeep<IMatchConfigEntry[]>;
type MatchConfigOrLiteral = typeof matchConfigOrLiteral;
export type MatchConfigOr = WritableDeep<MatchConfigOrLiteral>;

matchConfigZrLiteral satisfies ReadonlyDeep<IMatchConfigEntryZr[]>;
type MatchConfigZrLiteral = typeof matchConfigZrLiteral;
export type MatchConfigZr = WritableDeep<MatchConfigZrLiteral>;

matchConfigTfLiteral satisfies ReadonlyDeep<IMatchConfigEntryTf[]>;
type MatchConfigTfLiteral = typeof matchConfigTfLiteral;
export type MatchConfigTf = WritableDeep<MatchConfigTfLiteral>;

export type MatchConfigTypeToConfigKeyMap = {
  [EGameConfigs.MATCH_CONFIGS_OR]: MatchConfigOr;
  [EGameConfigs.MATCH_CONFIGS_ZR]: MatchConfigZr;
  [EGameConfigs.MATCH_CONFIGS_TF]: MatchConfigTf;
};
